import { Card, CardBody } from "@components/ui/Card"
import { useGradeScale } from "@hooks/useGradeScale"
import { formatGrade } from "@lib/intl"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { DirectionTrendPoint } from "../types"

interface Props {
  points: DirectionTrendPoint[]
}

interface ChartRow {
  label: string
  current: number
  previous: number
}

export const TrimesterTrendChart = memo(function TrimesterTrendChart({ points }: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()

  const data: ChartRow[] = useMemo(
    () =>
      points.map((p) => ({
        label: t("trend.week", { n: p.week_index }),
        current: p.current_avg,
        previous: p.previous_avg,
      })),
    [points, t],
  )

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-sm font-semibold">{t("trend.title")}</h2>
          <span className="text-ink-subtle text-xs">{t("trend.subtitle")}</span>
        </div>

        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.25)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "currentColor" }}
                stroke="currentColor"
                className="text-ink-subtle"
              />
              <YAxis
                // The y-axis domain still reads as a fraction of the
                // 0-10 scale on the wire; the formatter renders that
                // value in the professor's active scale.
                domain={[5, 10]}
                tick={{ fontSize: 11, fill: "currentColor" }}
                stroke="currentColor"
                className="text-ink-subtle"
                tickFormatter={(v: number) => formatGrade(v, scale)}
              />
              <Tooltip
                formatter={(value: number) => formatGrade(value, scale)}
                contentStyle={{
                  borderRadius: 8,
                  borderColor: "rgba(99, 102, 241, 0.4)",
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="previous"
                name={t("trend.legend.previous")}
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="current"
                name={t("trend.legend.current")}
                stroke="#6366f1"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardBody>
    </Card>
  )
})
